// @flow
import React, { useContext, useMemo, useEffect, useState } from 'react';
import styled from 'styled-components';
import type { Node } from 'react';
import DynamicTable from '@atlaskit/dynamic-table';
import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import MoreIcon from '@atlaskit/icon/glyph/more';
import AvatarGroup from '@atlaskit/avatar-group';
import DropdownMenu, { DropdownItemGroup, DropdownItem } from '@atlaskit/dropdown-menu';
import { token, useThemeObserver } from '@atlaskit/tokens';
import { SimpleTag } from '@atlaskit/tag';
import EditorWarningIcon from '@atlaskit/icon/glyph/editor/warning';
import Tooltip from '@atlaskit/tooltip';
import { formatInTimeZone } from 'date-fns-tz';
import { uniq, uniqBy } from 'lodash';
import Spinner from '@atlaskit/spinner';
import LoadingOverlay from 'react-loading-overlay';
import OctoPermissionsContext from '../../providers/OctoPermissionsContext';
import { hasPermission } from '../../utils/commons';
import { OCTO_GLOBAL_PERMISSIONS } from '../../utils/consts';
import IssuesProgressTracker from './IssuesProgressTracker';
import { getJiraProjects } from '../../services/jira-services';
import AvatarAndName from '../virtual-components/AvatarAndName';
import ICON_COMPONENT_VERSION_EMPTY from '../../assets/component-empty.svg';

const NO_PROJECT_PERMISSION_TOOLTIP = `You don't have permission on this component projects`;
const NO_COMPONENT_SELECTED_TOOLTIP = `There's no component selected for this component version`;

const Empty = styled.div`
    h3,
    p {
        margin-bottom: 14px;
    }
    img {
        width: 160px;
        margin-top: 14px;
    }
`;

function DropdownOptions({ editAction, deleteAction, releaseAction, isReleased }) {
    return (
        <DropdownItemGroup>
            <DropdownItem onClick={editAction}>Edit</DropdownItem>
            <DropdownItem onClick={releaseAction}>{isReleased ? 'Unrelease' : 'Release'}</DropdownItem>
            <DropdownItem onClick={deleteAction}>Delete</DropdownItem>
        </DropdownItemGroup>
    );
}

type GetTableRowsInput = {
    virtualComponents: Array<?VirtualComponent>,
    editVirtualComponentVersion: (virtualComponent: any) => void,
    setToDelete: Function,
    setToMerge: Function,
    allProjects: Array<?JiraProject>,
    baseInstanceUrl: string,
};

const MoreIconTrigger = (params) => {
    const { triggerRef, ...props } = params;
    return <IconButton {...props} icon={MoreIcon} label="more" ref={triggerRef} />;
};

const getProjectsContent = ({ releaseProjects, allProjects, isComponentValid }) => {
    if (!allProjects) {
        return <Spinner />;
    }

    const relatedIds = (releaseProjects ?? []).map(({ jiraProjectId }) => jiraProjectId);
    const avatarGroupData = (allProjects ?? [])
        .filter((jiraProject) => relatedIds.includes(jiraProject.id))
        .map((project) => ({
            key: project.jiraProjectId ?? project.id,
            name: project.jiraProjectName ?? project.name,
            src: project.jiraAvatarUrls ? project.jiraAvatarUrls.large : project.avatarUrls?.['48x48'],
            appearance: 'circle',
            size: 'medium',
            enableTooltip: true,
            onClick: () => window.PlatformAdapter.openProjectUrl(project.jiraProjectKey ?? project.key, project.style),
        }));

    if (avatarGroupData.length === 1) {
        const [entry] = avatarGroupData;
        return (
            <AvatarAndName
                displayName={entry.name}
                avatarSrc={entry.src}
                avatarHref={entry.href}
                avatarOnClick={entry.onClick}
            />
        );
    }

    if (avatarGroupData.length > 1) {
        return <AvatarGroup appearance="stack" size="medium" data={avatarGroupData} maxCount={4} />;
    }

    return (
        <Tooltip content={isComponentValid ? NO_PROJECT_PERMISSION_TOOLTIP : NO_COMPONENT_SELECTED_TOOLTIP}>
            <div style={{ display: 'flex', alignItems: 'center', color: '#FF4411' }}>
                <EditorWarningIcon color="#FF4411" />
                <span style={{ marginLeft: '5px' }}>None</span>
            </div>
        </Tooltip>
    );
};

function getTableRows({
    virtualComponentVersions,
    editVirtualComponentVersion,
    setToDelete,
    setReleaseStatus,
    projects,
}: GetTableRowsInput): Array {
    return (virtualComponentVersions || []).map((release) => {
        return {
            cells: [
                {
                    key: 'name',
                    content: (
                        <div
                            style={{ color: token('color.link'), cursor: 'pointer' }}
                            onClick={() => editVirtualComponentVersion(release)}
                        >
                            {release.name}
                        </div>
                    ),
                },
                {
                    key: 'virtualComponent.name',
                    content: release.virtualComponent ? (
                        release.virtualComponent.name
                    ) : (
                        <Tooltip content={NO_COMPONENT_SELECTED_TOOLTIP}>
                            <div style={{ display: 'flex', alignItems: 'center', color: '#FF4411' }}>
                                <EditorWarningIcon color="#FF4411" />
                                <span style={{ marginLeft: '5px' }}>None</span>
                            </div>
                        </Tooltip>
                    ),
                },
                {
                    key: 'projects',
                    content: getProjectsContent({
                        releaseProjects: release?.virtualComponent?.projects,
                        allProjects: projects,
                        isComponentValid: !!release.virtualComponent,
                    }),
                },
                {
                    key: 'isReleased',
                    content: release.isReleased ? (
                        <SimpleTag text="Released" color="greenLight" />
                    ) : (
                        <SimpleTag text="Unreleased" color="blueLight" />
                    ),
                },
                {
                    key: 'progress',
                    content: <IssuesProgressTracker releaseName={release.name} />,
                },
                {
                    key: 'startDate',
                    content: release.startDate
                        ? formatInTimeZone(new Date(`${release.startDate}`), 'UTC', 'MMM d, yyyy')
                        : '',
                },
                {
                    key: 'releaseDate',
                    content: release.releaseDate
                        ? formatInTimeZone(new Date(`${release.releaseDate}`), 'UTC', 'MMM d, yyyy')
                        : '',
                },
                {
                    key: 'action',
                    name: 'Action',
                    content: (
                        <OctoPermissionsContext.Consumer>
                            {(permissions) => {
                                if (hasPermission(OCTO_GLOBAL_PERMISSIONS.MANAGE, permissions)) {
                                    return (
                                        <DropdownMenu trigger={MoreIconTrigger} position="bottom right">
                                            <DropdownOptions
                                                editAction={() => editVirtualComponentVersion(release)}
                                                deleteAction={() => setToDelete(release)}
                                                releaseAction={() => setReleaseStatus(release)}
                                                isReleased={release.isReleased}
                                            />
                                        </DropdownMenu>
                                    );
                                }
                            }}
                        </OctoPermissionsContext.Consumer>
                    ),
                },
            ],
            key: `id-${release.id}`,
        };
    });
}

function EmptyView({
    setIsFormOpen,
    hasVirtualComponentVersions,
    hasFilterApplied,
}: {
    setIsFormOpen: (isFormOpen: boolean) => void,
    hasVirtualComponentVersions: boolean,
    hasFilterApplied: boolean,
}): Node {
    const permissions = useContext(OctoPermissionsContext);
    const message =
        hasVirtualComponentVersions || hasFilterApplied
            ? "We couldn't find any component versions that matched your search"
            : 'There are no component versions in your projects yet.';

    const subMessage =
        !hasVirtualComponentVersions && 'Component versions help you schedule and track releases of your components.';

    return (
        <Empty>
            {hasVirtualComponentVersions ? (
                <img alt="No results" src={`./no-results.jpeg`} />
            ) : (
                <img alt="component versions" src={ICON_COMPONENT_VERSION_EMPTY} width="170" height="87" />
            )}
            <h3>{message}</h3>
            {subMessage && <p>{subMessage}</p>}
            {!hasVirtualComponentVersions && hasPermission(OCTO_GLOBAL_PERMISSIONS.MANAGE, permissions) && (
                <Button appearance="primary" onClick={() => setIsFormOpen(true)}>
                    {hasVirtualComponentVersions ? 'Start versioning' : 'Create one'}
                </Button>
            )}
        </Empty>
    );
}

type Props = {
    virtualComponentVersions: Array<?VirtualComponentVersion>,
    openCreateVirtualComponentVersionModal: (isFormOpen: boolean) => void,
    editVirtualComponentVersion: (virtualComponent: VirtualComponentVersion) => void,
    setToDelete: Function,
    setReleaseStatus: Function,
    onSortingChange: Function,
    hasFilterApplied: boolean,
    sorting: {
        property: string,
        direction: 'asc' | 'desc',
    },
    isLoading?: boolean,
};

const headers: AKTableHead = {
    cells: [
        {
            key: 'name',
            content: 'Version name',
            isSortable: true,
        },
        {
            key: 'virtualComponent',
            content: 'Component',
            isSortable: true,
        },
        {
            key: 'project',
            content: 'Component projects',
            isSortable: false,
        },
        {
            key: 'status',
            content: 'Status',
            isSortable: true,
        },
        {
            key: 'progress',
            content: 'Progress',
            isSortable: false,
        },
        {
            key: 'startDate',
            content: 'Start date',
            isSortable: true,
        },
        {
            key: 'releaseDate',
            content: 'Release date',
            isSortable: true,
        },
    ],
};

const VirtualComponentVersionsTable: React.FC<Props> = ({
    virtualComponentVersions,
    openCreateVirtualComponentVersionModal,
    editVirtualComponentVersion,
    setToDelete,
    setReleaseStatus,
    hasFilterApplied,
    sorting,
    onSortingChange,
    isLoading = false,
}: Props) => {
    const [jiraProjects, setJiraProjects] = useState(undefined);

    useEffect(() => {
        (async () => {
            const currentProjectsIds = (jiraProjects ?? []).map(({ id }) => id);
            const projectsIdsToGet = uniq(
                virtualComponentVersions.flatMap((release) =>
                    (release?.virtualComponent?.projects ?? []).map((p) => p.jiraProjectId),
                ),
            ).filter((id) => !currentProjectsIds.includes(id));

            if (projectsIdsToGet?.length) {
                const newProjects = await getJiraProjects({
                    jiraProjectIds: projectsIdsToGet,
                    maxParallelRequests: 20,
                });

                if (newProjects?.length) {
                    setJiraProjects((prev) => {
                        return uniqBy([...(prev ?? []), ...(newProjects ?? [])], 'id');
                    });
                } else if (jiraProjects === undefined) {
                    setJiraProjects([]);
                }
            }
        })();
    }, [virtualComponentVersions, jiraProjects]);

    const rows = useMemo(() => {
        return getTableRows({
            virtualComponentVersions,
            editVirtualComponentVersion,
            setToDelete,
            setReleaseStatus,
            projects: jiraProjects,
        });
    }, [editVirtualComponentVersion, virtualComponentVersions, jiraProjects, setReleaseStatus, setToDelete]);
    const { colorMode } = useThemeObserver();
    return (
        <LoadingOverlay
            active={!!isLoading}
            spinner={<Spinner />}
            styles={{
                overlay: (base) => ({
                    ...base,
                    background: colorMode === 'dark' ? 'rgba(35, 35, 40, 0.5)' : 'rgba(235, 236, 240, 0.3)',
                }),
            }}
        >
            <DynamicTable
                emptyView={
                    <EmptyView
                        setIsFormOpen={openCreateVirtualComponentVersionModal}
                        hasVirtualComponentVersions={virtualComponentVersions && !!virtualComponentVersions.length}
                        hasFilterApplied={hasFilterApplied}
                    />
                }
                rows={rows}
                head={headers}
                isLoading={false}
                onSort={onSortingChange}
                sortKey={sorting.property}
                sortOrder={sorting.direction?.toUpperCase()}
                isFixedSize
            />
        </LoadingOverlay>
    );
};

export default VirtualComponentVersionsTable;
